import React, { useState, useEffect, Suspense } from 'react';
import styled from 'styled-components';
import { GallerySkeleton } from './GallerySkeleton';

const LandingPageGallery = React.lazy(() => import('./LandingPageGallery'));

// Wrapper to contain both gallery and skeleton overlay
const GalleryWrapper = styled.div`
  position: relative;
  width: 100%;
`;

// Overlay that fades out to reveal the real gallery underneath
// Positioned absolutely so it never affects layout
const SkeletonOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
  opacity: ${props => (props.$isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-out;
  pointer-events: ${props => (props.$isVisible ? 'auto' : 'none')};
  visibility: ${props => (props.$isVisible ? 'visible' : 'hidden')};
`;

// Wrapper for the real gallery
const GalleryContainer = styled.div<{ $isLoading: boolean }>`
  position: relative;
  width: 100%;
  min-height: ${props => (props.$isLoading ? '400px' : 'auto')};
  max-height: ${props => (props.$isLoading ? '400px' : 'none')};
  overflow: ${props => (props.$isLoading ? 'hidden' : 'visible')};
  opacity: ${props => (props.$isLoading ? 0 : 1)};
  transition: opacity 0.2s ease-in;
`;

// Track if gallery has been mounted before (persists across remounts)
let hasBeenMountedBefore = false;

type Props = {
  skipSkeleton?: boolean;
};

/**
 * Wrapper component that provides smooth cross-fade transition from skeleton to real gallery.
 *
 * Flow:
 * 1. Initial render: Show skeleton immediately (lazy loading always has delay)
 * 2. Gallery loads data underneath (but stays hidden)
 * 3. Once gallery data loads, keep both hidden for 700ms
 * 4. Then simultaneously: fade out skeleton AND fade in gallery (300ms CSS transition)
 * 5. Remove skeleton from DOM after fade completes
 *
 * If skipSkeleton is true or gallery has been mounted before, skeleton is skipped.
 */
const LandingPageGalleryWithTransition: React.FC<Props> = ({ skipSkeleton }) => {
  // Skip skeleton if explicitly requested or if previously mounted
  const shouldSkipSkeleton = skipSkeleton || hasBeenMountedBefore;

  const [isLoaded, setIsLoaded] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(!shouldSkipSkeleton);
  const [skeletonVisible, setSkeletonVisible] = useState(!shouldSkipSkeleton);
  const [galleryVisible, setGalleryVisible] = useState(shouldSkipSkeleton);

  // Mark as mounted once loaded (for subsequent visits)
  useEffect(() => {
    if (isLoaded) {
      hasBeenMountedBefore = true;
    }
  }, [isLoaded]);

  // Wait 700ms after gallery loads, then start cross-fade
  useEffect(() => {
    if (isLoaded) {
      const fadeTimer = setTimeout(() => {
        setSkeletonVisible(false); // Start CSS fade-out of skeleton
        setGalleryVisible(true); // Start CSS fade-in of gallery

        // Remove skeleton from DOM after CSS transition completes (300ms)
        setTimeout(() => {
          setShowSkeleton(false);
        }, 300);
      }, 700); // Keep skeleton visible for 700ms after gallery loads

      return () => clearTimeout(fadeTimer);
    }
  }, [isLoaded]);

  return (
    <GalleryWrapper>
      {/* Real gallery - sets the height/layout, stays hidden until cross-fade starts */}
      <GalleryContainer $isLoading={!galleryVisible}>
        <Suspense fallback={null}>
          <LoadedTracker onLoad={() => setIsLoaded(true)}>
            <LandingPageGallery />
          </LoadedTracker>
        </Suspense>
      </GalleryContainer>

      {/* Skeleton overlay - stays visible for 700ms, then fades out */}
      {showSkeleton && (
        <SkeletonOverlay $isVisible={skeletonVisible}>
          <GallerySkeleton />
        </SkeletonOverlay>
      )}
    </GalleryWrapper>
  );
};

// Helper component that notifies parent when data has loaded
const LoadedTracker: React.FC<{ onLoad: () => void; children: React.ReactNode }> = ({
  onLoad,
  children
}) => {
  const childElement = React.Children.only(children);

  // Clone the child and inject onDataLoaded callback
  return React.cloneElement(childElement as React.ReactElement, {
    onDataLoaded: () => {
      // Call immediately to avoid unnecessary delay
      onLoad();
    }
  });
};

export default LandingPageGalleryWithTransition;
